import { apiFetchJson } from '../http/apiFetch';
import type {
  AssignmentRow,
  AssignmentTargetType,
  FeatureFlag,
  FeatureFlagAuditEntry,
  FeatureFlagCreatePayload,
  FeatureFlagDetail,
  FeatureFlagFilters,
  FeatureFlagSortKey,
  FeatureFlagUpdatePayload,
  SortDirection,
} from '../../models/featureFlag';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asBoolean(value: unknown): boolean {
  return value === true || value === 'true';
}

function asCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizeFeatureFlag(row: unknown): FeatureFlag | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const f = row as Record<string, unknown>;
  const id = asString(f.id ?? f.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    key: asString(f.key ?? f.Key).trim(),
    description: asOptionalString(f.description ?? f.Description),
    enabledGlobally: asBoolean(f.enabledGlobally ?? f.EnabledGlobally),
    isArchived: asBoolean(f.isArchived ?? f.IsArchived),
    createdBy: asOptionalString(f.createdBy ?? f.CreatedBy),
    createdAt: asString(f.createdAt ?? f.CreatedAt),
    updatedAt: asString(f.updatedAt ?? f.UpdatedAt),
    assignedUserCount: asCount(f.assignedUserCount ?? f.AssignedUserCount),
    assignedCompanyCount: asCount(f.assignedCompanyCount ?? f.AssignedCompanyCount),
    assignedRoleCount: asCount(f.assignedRoleCount ?? f.AssignedRoleCount),
  };
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((v) => String(v)).filter((v) => v.trim() !== '');
}

export function normalizeAssignmentRow(row: unknown): AssignmentRow | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  const id = asString(r.id ?? r.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    displayName: asString(r.displayName ?? r.DisplayName, id),
  };
}

function asAssignmentRowArray(value: unknown): AssignmentRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(normalizeAssignmentRow).filter((row): row is AssignmentRow => Boolean(row));
}

export function normalizeFeatureFlagDetail(row: unknown): FeatureFlagDetail | null {
  const base = normalizeFeatureFlag(row);
  if (!base) {
    return null;
  }
  const f = row as Record<string, unknown>;
  return {
    ...base,
    companies: asAssignmentRowArray(f.companies ?? f.Companies),
    users: asAssignmentRowArray(f.users ?? f.Users),
    roles: asStringArray(f.roles ?? f.Roles),
    availableRoles: asStringArray(f.availableRoles ?? f.AvailableRoles),
  };
}

export type FeatureFlagEffectiveStatus = 'global' | 'targeted' | 'inactive';

/**
 * A flag with EnabledGlobally off is NOT fully disabled if it still has company/user/role
 * assignments — FeatureFlagService.Resolve (backend) falls through to those after the global
 * check. This classifies what the flag actually does at runtime, for display purposes only.
 */
export function getFeatureFlagEffectiveStatus(
  isArchived: boolean,
  enabledGlobally: boolean,
  totalAssignedCount: number
): FeatureFlagEffectiveStatus {
  if (isArchived) {
    return 'inactive';
  }
  if (enabledGlobally) {
    return 'global';
  }
  return totalAssignedCount > 0 ? 'targeted' : 'inactive';
}

export function describeFeatureFlagEffectiveStatus(
  status: FeatureFlagEffectiveStatus,
  totalAssignedCount: number
): string {
  if (status === 'global') {
    return 'Global';
  }
  if (status === 'targeted') {
    return `Targeted (${totalAssignedCount})`;
  }
  return 'Inactive';
}

/** Emits a lightweight client-side audit signal (backend is the authoritative trail). */
export function logFeatureFlagAudit(
  event: 'access' | 'filter' | 'create' | 'update' | 'archive' | 'assign' | 'unassign',
  details: Record<string, unknown>
): void {
  const payload = { event, timestamp: new Date().toISOString(), ...details };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bo-admin-feature-flag-audit', { detail: payload }));
  }
  console.info('[AdminFeatureFlagAudit]', payload);
}

export async function fetchFeatureFlags(
  getAccessToken: () => string | undefined,
  filters?: FeatureFlagFilters
): Promise<FeatureFlag[]> {
  const params = new URLSearchParams();
  if (filters?.status && filters.status.toLowerCase() !== 'all') {
    params.set('status', filters.status);
  }
  if (filters?.search?.trim()) {
    params.set('search', filters.search.trim());
  }
  const query = params.toString();
  const path = `/api/admin/feature-flags${query ? `?${query}` : ''}`;

  const result = await apiFetchJson<unknown>(path, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  const rows = Array.isArray(result)
    ? result
    : result && typeof result === 'object' && Array.isArray((result as { value?: unknown }).value)
      ? (result as { value: unknown[] }).value
      : [];
  return rows.map(normalizeFeatureFlag).filter((f): f is FeatureFlag => Boolean(f));
}

export async function createFeatureFlag(
  payload: FeatureFlagCreatePayload,
  getAccessToken: () => string | undefined
): Promise<FeatureFlag | null> {
  const result = await apiFetchJson<unknown>('/api/admin/feature-flags', {
    method: 'POST',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeFeatureFlag(result);
}

export async function updateFeatureFlag(
  id: string,
  payload: FeatureFlagUpdatePayload,
  getAccessToken: () => string | undefined
): Promise<FeatureFlag | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/feature-flags/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeFeatureFlag(result);
}

export async function fetchFeatureFlagDetail(
  id: string,
  getAccessToken: () => string | undefined
): Promise<FeatureFlagDetail | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/feature-flags/${encodeURIComponent(id)}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  return normalizeFeatureFlagDetail(result);
}

export function normalizeFeatureFlagAuditEntry(row: unknown): FeatureFlagAuditEntry | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const e = row as Record<string, unknown>;
  const id = asString(e.id ?? e.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    outcome: asString(e.outcome ?? e.Outcome),
    email: asOptionalString(e.email ?? e.Email),
    reason: asOptionalString(e.reason ?? e.Reason),
    metadata: asOptionalString(e.metadata ?? e.Metadata),
    createdAt: asString(e.createdAt ?? e.CreatedAt),
  };
}

export async function fetchFeatureFlagAuditHistory(
  id: string,
  getAccessToken: () => string | undefined
): Promise<FeatureFlagAuditEntry[]> {
  const result = await apiFetchJson<unknown>(`/api/admin/feature-flags/${encodeURIComponent(id)}/audit`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  const rows = Array.isArray(result) ? result : [];
  return rows.map(normalizeFeatureFlagAuditEntry).filter((e): e is FeatureFlagAuditEntry => Boolean(e));
}

export async function fetchFeatureFlagAssignmentTargets(
  type: AssignmentTargetType,
  search: string | undefined,
  getAccessToken: () => string | undefined
): Promise<AssignmentRow[]> {
  const params = new URLSearchParams({ type });
  if (search?.trim()) {
    params.set('search', search.trim());
  }
  const result = await apiFetchJson<unknown>(`/api/admin/feature-flags/assignment-targets?${params.toString()}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  return Array.isArray(result) ? result.map(normalizeAssignmentRow).filter((row): row is AssignmentRow => Boolean(row)) : [];
}

/** Client-side filter over the already-fetched list (mirrors the backend status/search filter). */
export function filterFeatureFlags(flags: FeatureFlag[], filters: FeatureFlagFilters): FeatureFlag[] {
  const normalizedStatus = (filters.status ?? '').trim().toLowerCase();
  const normalizedSearch = (filters.search ?? '').trim().toLowerCase();

  return flags.filter((flag) => {
    const matchesStatus =
      !normalizedStatus ||
      normalizedStatus === 'all' ||
      (normalizedStatus === 'active' && !flag.isArchived) ||
      (normalizedStatus === 'archived' && flag.isArchived);
    const matchesSearch =
      !normalizedSearch ||
      flag.key.toLowerCase().includes(normalizedSearch) ||
      (flag.description ?? '').toLowerCase().includes(normalizedSearch);
    return matchesStatus && matchesSearch;
  });
}

function compareFlags(a: FeatureFlag, b: FeatureFlag, key: FeatureFlagSortKey): number {
  switch (key) {
    case 'enabledGlobally':
      return Number(a.enabledGlobally) - Number(b.enabledGlobally);
    case 'isArchived':
      return Number(a.isArchived) - Number(b.isArchived);
    case 'createdAt':
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    case 'key':
    default:
      return a.key.localeCompare(b.key, undefined, { sensitivity: 'base' });
  }
}

/** Returns a new array sorted by the given key/direction. Does not mutate the input. */
export function sortFeatureFlags(
  flags: FeatureFlag[],
  sortKey: FeatureFlagSortKey,
  sortDirection: SortDirection
): FeatureFlag[] {
  return [...flags].sort((a, b) => {
    const result = compareFlags(a, b, sortKey);
    return sortDirection === 'asc' ? result : -result;
  });
}
